import { ActionReducer } from '@ngrx/store';

import * as AuthActions from './auth/auth.actions';

/**
 * AD-3. Throw the whole store away when an admin or counselor signs out.
 *
 * The MEMBER logout does this already, though not obviously: it clears both web
 * storages and then sets `window.location.href = '/'`, so the document reloads
 * and the store is reconstructed from nothing. A total reset achieved by
 * nuking the page.
 *
 * The ADMIN logout deletes the token, clears the user profile and
 * router-navigates. No reload. Around twenty NgRx slices -- goal, institution,
 * aiChat, favoriteOccupations, occupationDetail, pathway, tracker and the rest
 * -- survive in memory, and whether the next person sees any of it depends on
 * every component happening to re-fetch on mount. On a shared workstation that
 * is not a guarantee worth relying on.
 *
 * A meta-reducer is the right place because it is the only thing that can reach
 * EVERY slice, including ones added later by someone who never reads this file.
 * Per-feature reset actions do exist -- resetGoalState, clearAllGoals,
 * resetAiChatState -- and NONE of them is dispatched from either logout path,
 * which is exactly the kind of gap this avoids.
 *
 * Keyed on adminLogOutSuccess rather than adminLogOut: the sign-out effect
 * chain has to finish dispatching before the slices it touches are discarded.
 *
 * ---------------------------------------------------------------------------
 * In its own file, rather than beside the other meta-reducer in state/index.ts,
 * for a reason worth knowing: index.ts imports
 * `@larscom/ngrx-store-storagesync`, which pulls in lodash-es, which jest
 * cannot parse. Anything that lives there is testable only by reading its
 * source. This is a security-adjacent behaviour on a shared workstation and
 * deserves real assertions, so it lives where they can be written.
 */
export function clearStateOnAdminLogOut<T>(
  reducer: ActionReducer<T>
): ActionReducer<T> {
  return (state, action) =>
    reducer(
      action.type === AuthActions.adminLogOutSuccess.type ? undefined : state,
      action
    );
}
